import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Avatar, Box, Button, CircularProgress, IconButton, InputBase, Stack, Tooltip, Typography, Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ImageIcon from '@mui/icons-material/Image';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import VideocamIcon from '@mui/icons-material/Videocam';
import CallIcon from '@mui/icons-material/Call';
import DownloadIcon from '@mui/icons-material/Download';
import GroupIcon from '@mui/icons-material/Group';
import PublicIcon from '@mui/icons-material/Public';
import axios from 'axios';
import moment from 'moment';
import ApiLink from 'API';
import { useChat } from 'contexts/ChatContext';
import AuthImage, { downloadChatFile } from './AuthImage';
import ImageLightbox from './ImageLightbox';
import MembersDialog from './MembersDialog';
import { getFileMeta, formatFileSize } from './fileIcon';

function senderName(m) {
    const s = m.senderId;
    if (!s || typeof s === 'string') return '';
    return s.fullname || s.username || '';
}

export default function ChatRoom({ room, onBack, onStartCall, onJoinCall, inCall, onClose }) {
    const { myId, emit, on, socket, setActiveRoomId, markRoomRead, authHeaders, activeCalls, onlineUsers } = useChat();
    const roomId = room._id;
    const ongoingCall = activeCalls && activeCalls[roomId]; // a live call you can join

    const [messages, setMessages] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [text, setText] = useState('');
    const [uploading, setUploading] = useState(false);
    const [typingUsers, setTypingUsers] = useState({});
    const [lightbox, setLightbox] = useState(null); // { messageId, name }
    const [membersOpen, setMembersOpen] = useState(false);

    const listRef = useRef(null);
    const bottomRef = useRef(null);
    const fileInputRef = useRef(null);
    const imageInputRef = useRef(null);
    const typingTimer = useRef(null);

    const scrollToBottom = (smooth = false) => {
        requestAnimationFrame(() => {
            bottomRef.current && bottomRef.current.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
        });
    };

    // Append a message (deduped by _id), then scroll. Used by both the realtime
    // echo and the send-ack so the sender always sees their own message instantly.
    const appendMessage = (m) => {
        if (!m || !m._id) return;
        setMessages((prev) => (prev.some((x) => x._id === m._id) ? prev : [...prev, m]));
        scrollToBottom(true);
    };

    // initial load
    const loadInitial = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${ApiLink}/api/chat/rooms/${roomId}/messages?limit=50`, authHeaders());
            setMessages(res.data.messages || []);
            setHasMore(res.data.hasMore);
        } catch (e) { /* ignore */ } finally {
            setLoading(false);
            scrollToBottom();
        }
    }, [roomId]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadMore = async () => {
        if (messages.length === 0) return;
        setLoadingMore(true);
        const prevHeight = listRef.current ? listRef.current.scrollHeight : 0;
        try {
            const oldest = messages[0]._id;
            const res = await axios.get(`${ApiLink}/api/chat/rooms/${roomId}/messages?before=${oldest}&limit=50`, authHeaders());
            setMessages((prev) => [...(res.data.messages || []), ...prev]);
            setHasMore(res.data.hasMore);
            // preserve scroll position
            requestAnimationFrame(() => {
                if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight - prevHeight;
            });
        } catch (e) { /* ignore */ } finally {
            setLoadingMore(false);
        }
    };

    // join room + register socket listeners
    useEffect(() => {
        setActiveRoomId(roomId);
        markRoomRead(roomId);
        emit('chat:join_room', roomId);
        loadInitial();

        const offMsg = on('chat:message_received', (m) => {
            if (m.roomId !== roomId && m.roomId?.toString?.() !== roomId) return;
            appendMessage(m);
            emit('chat:read_messages', { roomId });
        });
        const offTyping = on('chat:user_typing', ({ roomId: rid, userId, fullname }) => {
            if (rid !== roomId || userId === myId) return;
            setTypingUsers((prev) => ({ ...prev, [userId]: fullname || 'Someone' }));
        });
        const offStop = on('chat:user_stop_typing', ({ roomId: rid, userId }) => {
            if (rid !== roomId) return;
            setTypingUsers((prev) => { const n = { ...prev }; delete n[userId]; return n; });
        });

        return () => {
            emit('chat:leave_room', roomId);
            setActiveRoomId(null);
            offMsg && offMsg();
            offTyping && offTyping();
            offStop && offStop();
        };
    }, [roomId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleTyping = (v) => {
        setText(v);
        emit('chat:typing', { roomId });
        if (typingTimer.current) clearTimeout(typingTimer.current);
        typingTimer.current = setTimeout(() => emit('chat:stop_typing', { roomId }), 1200);
    };

    const sendText = () => {
        const body = text.trim();
        if (!body) return;
        emit('chat:send_message', { roomId, content: body, type: 'text' }, (resp) => {
            if (resp && resp.ok && resp.message) appendMessage(resp.message);
        });
        setText('');
        emit('chat:stop_typing', { roomId });
    };

    const uploadAndSend = async (file, kind) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await axios.post(`${ApiLink}/api/chat/upload`, fd, {
                withCredentials: true,
                headers: { token: localStorage.getItem('token'), 'Content-Type': 'multipart/form-data' },
            });
            emit('chat:send_message', {
                roomId,
                type: kind, // 'image' | 'file'
                content: '',
                file: res.data,
            }, (resp) => {
                if (resp && resp.ok && resp.message) appendMessage(resp.message);
            });
        } catch (err) {
            console.error('upload error', err?.response?.data?.msg || err.message);
        } finally {
            setUploading(false);
        }
    };

    // Mirror of the backend blocklist for instant UX feedback (backend is authoritative)
    const BLOCKED_EXT = ['exe', 'bat', 'cmd', 'sh', 'js', 'vbs', 'scr', 'com', 'pif', 'msi',
        'gadget', 'wsf', 'php', 'py', 'pl', 'jsp', 'asp', 'aspx', 'html', 'htm', 'xhtml'];

    const handleFile = async (e, kind) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        if (BLOCKED_EXT.includes(ext)) {
            alert(`ປະເພດໄຟລ໌ .${ext} ບໍ່ອະນຸຍາດ (blocked file type)`);
            return;
        }
        await uploadAndSend(file, kind);
    };

    // Ctrl+V an image from clipboard → upload + send as image
    const handlePaste = (e) => {
        const items = e.clipboardData && e.clipboardData.items;
        if (!items) return;
        for (const item of items) {
            if (item.type && item.type.startsWith('image/')) {
                const blob = item.getAsFile();
                if (blob) {
                    e.preventDefault();
                    // give pasted blobs a sensible name + extension
                    const ext = (item.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
                    const named = new File([blob], `pasted-${Date.now()}.${ext}`, { type: item.type });
                    uploadAndSend(named, 'image');
                }
                return;
            }
        }
    };

    const isGroup = room.type === 'group' || room.type === 'global';
    const isGlobal = room.type === 'global';
    const typingNames = Object.values(typingUsers);
    // online count: all online users for Global, else online participants of this room
    const onlineCount = isGlobal
        ? Object.values(onlineUsers).filter(Boolean).length
        : (room.participants || []).filter((p) => onlineUsers[(p._id || p).toString()]).length;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <IconButton size="small" onClick={onBack}><ArrowBackIcon /></IconButton>
                <Avatar sx={{ width: 32, height: 32, bgcolor: isGlobal ? 'success.main' : 'primary.main' }}>
                    {isGlobal ? <PublicIcon fontSize="small" /> : <GroupIcon fontSize="small" />}
                </Avatar>
                <Box
                    sx={{ flex: 1, minWidth: 0, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                    onClick={() => setMembersOpen(true)}
                    title="ເບິ່ງສະມາຊິກ"
                >
                    <Typography variant="subtitle1" noWrap>{room.name || (isGlobal ? '🌐 Global' : isGroup ? 'Group' : 'Chat')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {isGlobal ? 'ທຸກຄົນ' : `${(room.participants || []).length} ສະມາຊິກ`}
                        {' · '}
                        <Box component="span" sx={{ color: '#10b981', fontWeight: 600 }}>
                            🟢 {onlineCount} ອອນລາຍ
                        </Box>
                    </Typography>
                </Box>
                <Tooltip title="Voice call">
                    <IconButton color="success" onClick={() => onStartCall('voice')}><CallIcon /></IconButton>
                </Tooltip>
                <Tooltip title="Video call">
                    <IconButton color="primary" onClick={() => onStartCall('video')}><VideocamIcon /></IconButton>
                </Tooltip>
                {onClose && (
                    <Tooltip title="Close">
                        <IconButton onClick={onClose}><CloseIcon /></IconButton>
                    </Tooltip>
                )}
            </Stack>

            {/* Ongoing call → join anytime (persists across refresh) */}
            {ongoingCall && !inCall && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 1, bgcolor: '#0F5E5C', color: '#fff' }}>
                    {ongoingCall.kind === 'video' ? <VideocamIcon fontSize="small" /> : <CallIcon fontSize="small" />}
                    <Typography variant="body2" sx={{ flex: 1, color: '#fff' }}>ມີສາຍໂທຢູ່ໃນຫ້ອງນີ້</Typography>
                    <Button size="small" variant="contained" sx={{ bgcolor: '#fff', color: '#0F5E5C', '&:hover': { bgcolor: '#e8f5e9' } }}
                        onClick={() => onJoinCall && onJoinCall(ongoingCall.kind)}>
                        ເຂົ້າຮ່ວມ
                    </Button>
                </Stack>
            )}

            {/* Messages */}
            <Box ref={listRef} sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1, bgcolor: 'background.default' }}>
                {loading ? (
                    <Box sx={{ textAlign: 'center', pt: 4 }}><CircularProgress size={24} /></Box>
                ) : (
                    <>
                        {hasMore && (
                            <Box sx={{ textAlign: 'center', mb: 1 }}>
                                <Button size="small" onClick={loadMore} disabled={loadingMore}>
                                    {loadingMore ? 'ກຳລັງໂຫລດ…' : 'ໂຫລດເພີ່ມ (Load more)'}
                                </Button>
                            </Box>
                        )}
                        {messages.map((m) => {
                            const mine = (m.senderId?._id || m.senderId) === myId;
                            if (m.type === 'system' || m.type === 'call') {
                                const label = m.type === 'call'
                                    ? (m.call?.event === 'started' ? `📞 ${m.call?.kind === 'video' ? 'Video' : 'Voice'} call started` : '📵 Call ended')
                                    : m.content;
                                return (
                                    <Box key={m._id} sx={{ textAlign: 'center', my: 1 }}>
                                        <Chip size="small" label={label} variant="outlined" />
                                    </Box>
                                );
                            }
                            return (
                                <Box key={m._id} sx={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', mb: 0.75 }}>
                                    <Box sx={{ maxWidth: '78%' }}>
                                        {isGroup && !mine && (
                                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>{senderName(m)}</Typography>
                                        )}
                                        <Box
                                            sx={{
                                                px: 1.5, py: 0.75, borderRadius: 2,
                                                bgcolor: mine ? 'primary.main' : 'background.paper',
                                                color: mine ? '#fff' : 'text.primary',
                                                border: mine ? 'none' : '1px solid', borderColor: 'divider',
                                                boxShadow: 1,
                                            }}
                                        >
                                            {m.type === 'text' && (
                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: mine ? '#fff' : undefined }}>{m.content}</Typography>
                                            )}
                                            {m.type === 'image' && (
                                                <AuthImage
                                                    roomId={roomId}
                                                    messageId={m._id}
                                                    alt={m.file?.name}
                                                    onClick={() => setLightbox({ messageId: m._id, name: m.file?.name })}
                                                />
                                            )}
                                            {m.type === 'file' && (() => {
                                                const { Icon, color } = getFileMeta(m.file?.ext || m.file?.name);
                                                return (
                                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 180 }}>
                                                        <Icon sx={{ color, fontSize: 36 }} />
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography variant="body2" noWrap sx={{ maxWidth: 150, color: mine ? '#fff' : undefined }}>{m.file?.name}</Typography>
                                                            <Typography variant="caption" color={mine ? 'inherit' : 'text.secondary'}>{formatFileSize(m.file?.size)}</Typography>
                                                        </Box>
                                                        <IconButton size="small" sx={{ color: mine ? 'inherit' : color }} onClick={() => downloadChatFile(roomId, m._id, m.file?.name)}>
                                                            <DownloadIcon fontSize="small" />
                                                        </IconButton>
                                                    </Stack>
                                                );
                                            })()}
                                            <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', opacity: 0.6, mt: 0.25, color: mine ? '#fff' : undefined }}>
                                                {moment(m.createdAt).format('HH:mm')}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })}
                        <div ref={bottomRef} />
                    </>
                )}
            </Box>

            {/* Typing indicator */}
            {typingNames.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 0.25 }}>
                    {typingNames.join(', ')} ກຳລັງພິມ…
                </Typography>
            )}

            {/* Composer */}
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                <input ref={imageInputRef} type="file" accept="image/*" hidden onChange={(e) => handleFile(e, 'image')} />
                <input ref={fileInputRef} type="file" hidden onChange={(e) => handleFile(e, 'file')} />
                <Tooltip title="ສົ່ງຮູບ">
                    <IconButton size="small" onClick={() => imageInputRef.current?.click()} disabled={uploading}><ImageIcon /></IconButton>
                </Tooltip>
                <Tooltip title="ແນບໄຟລ์">
                    <IconButton size="small" onClick={() => fileInputRef.current?.click()} disabled={uploading}><AttachFileIcon /></IconButton>
                </Tooltip>
                <InputBase
                    sx={{ flex: 1, px: 1.5, py: 0.5, bgcolor: 'action.hover', borderRadius: 4 }}
                    placeholder={uploading ? 'ກຳລັງອັບໂຫລດ…' : 'ພິມຂໍ້ຄວາມ…'}
                    value={text}
                    onChange={(e) => handleTyping(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendText(); } }}
                    onPaste={handlePaste}
                    multiline
                    maxRows={4}
                />
                <IconButton color="primary" onClick={sendText} disabled={!text.trim()}><SendIcon /></IconButton>
            </Stack>

            {lightbox && (
                <ImageLightbox
                    roomId={roomId}
                    messageId={lightbox.messageId}
                    fileName={lightbox.name}
                    onClose={() => setLightbox(null)}
                />
            )}

            <MembersDialog open={membersOpen} onClose={() => setMembersOpen(false)} room={room} />
        </Box>
    );
}
